import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { WhatsAppBroadcastRecipient } from "../types";
import { BroadcastBoardCard } from "./BroadcastBoardCard";
import type { BroadcastBoardColumn as BoardColumn } from "./broadcastBoard";

const PAGE_SIZE = 30;

export function BroadcastBoardColumn({
  column,
  eligibleTotal,
  onSelect,
}: {
  column: BoardColumn;
  eligibleTotal: number;
  onSelect: (recipient: WhatsAppBroadcastRecipient) => void;
}) {
  // Recipients are already in memory (one broadcast ≤ a few thousand rows), so
  // "load more" is a pure client-side slice — no network. Re-keyed by the parent
  // on broadcast change so this resets.
  const [visible, setVisible] = useState(PAGE_SIZE);
  const shown = column.recipients.slice(0, visible);
  const hasMore = column.recipients.length > visible;
  const pct =
    eligibleTotal > 0 ? Math.round((column.total / eligibleTotal) * 100) : null;

  return (
    <div className="flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="truncate text-sm font-semibold" title={column.label}>
          {column.label}
        </span>
        <Badge variant="secondary" className="tabular-nums">
          {column.total}
          {pct != null ? ` · ${pct}%` : ""}
        </Badge>
      </div>
      <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto p-2">
        {shown.length === 0 ? (
          <p className="px-1 py-8 text-center text-xs text-muted-foreground">
            No contacts here.
          </p>
        ) : (
          shown.map((recipient) => (
            <BroadcastBoardCard
              key={String(recipient.id)}
              recipient={recipient}
              onSelect={onSelect}
            />
          ))
        )}
        {hasMore ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setVisible((value) => value + PAGE_SIZE)}
          >
            Load more
          </Button>
        ) : null}
      </div>
    </div>
  );
}
